/**
 * RetailOrderAutomationPage — Orders & Automation tab
 * =====================================================
 *
 * Purpose: incoming retailer orders, allocations, backorders and
 * replenishment decisions. The 4-strategy comparison opens in a
 * drawer, not inline.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { API_BASE } from '../../../lib/apiBase';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../../components/ui/sheet';
import {
    Loader2, Workflow, Check, ArrowDown, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import {
    useDistributionDemo,
    computeInsightStatus,
} from './DistributionDemoContext';
import PageSummary from './components/PageSummary';
import SmartRecommendation from './components/SmartRecommendation';
import InsightStatusBadge from './components/InsightStatusBadge';

const API = `${API_BASE}/api`;

const formatEur = (n) => `€${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function RetailOrderAutomationPage() {
    const { sim } = useOutletContext() || {};
    const [loading, setLoading] = useState(true);
    const [inventoryData, setInventoryData] = useState(null);
    const [replenishmentData, setReplenishmentData] = useState(null);
    const [compareSku, setCompareSku] = useState(null);
    const {
        publishedChannels, allocationDecisions, setAllocationDecisions,
        approvedReplenishments, selectedStrategy,
        warehouseTransfer, leadTimeOverride,
    } = useDistributionDemo();

    useEffect(() => {
        Promise.all([
            axios.get(`${API}/admin/distribution-demo/inventory`),
            axios.get(`${API}/admin/distribution-demo/replenishment`),
        ])
            .then(([inv, rpl]) => {
                setInventoryData(inv.data);
                setReplenishmentData(rpl.data);
            })
            .catch(err => {
                console.error('Failed to load automation data', err);
                toast.error('Failed to load data');
            })
            .finally(() => setLoading(false));
    }, []);

    const visibleOrders = useMemo(() => {
        if (!inventoryData) return [];
        const anyToggle = Object.values(publishedChannels).some(Boolean);
        if (!anyToggle) return inventoryData.retailer_orders;
        return inventoryData.retailer_orders.filter(o => publishedChannels[o.channel_id]);
    }, [inventoryData, publishedChannels]);

    const pendingCount = useMemo(() => {
        let count = 0;
        visibleOrders.forEach(o => o.lines.forEach(l => {
            const key = `${o.order_id}:${l.sku}`;
            const d = allocationDecisions[key] ?? (o.status === 'allocated' ? 'allocated' : null);
            if (!d) count += 1;
        }));
        return count;
    }, [visibleOrders, allocationDecisions]);

    const processedCount = useMemo(() => {
        let count = 0;
        visibleOrders.forEach(o => o.lines.forEach(l => {
            const key = `${o.order_id}:${l.sku}`;
            const d = allocationDecisions[key] ?? (o.status === 'allocated' ? 'allocated' : null);
            if (d) count += 1;
        }));
        return count;
    }, [visibleOrders, allocationDecisions]);

    const approvedCount = Object.values(approvedReplenishments).filter(Boolean).length;

    if (loading) {
        return <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>;
    }
    if (!inventoryData || !replenishmentData) return null;

    const { channels } = inventoryData;
    const channelMap = Object.fromEntries(channels.map(c => [c.channel_id, c]));
    const { suggestions, options_by_sku = {} } = replenishmentData;

    const projections = sim?.projections || [];
    const projectionBySku = Object.fromEntries(projections.map(p => [p.sku, p]));

    const summaryMetrics = [
        {
            testid: 'kpi-pending-allocations',
            label: 'Pending allocations',
            value: pendingCount,
            accent: pendingCount > 0 ? 'text-amber-600' : 'text-emerald-600',
            hint: pendingCount > 0 ? 'Awaiting your decision' : 'All lines actioned',
        },
        {
            testid: 'kpi-orders-processed',
            label: 'Orders processed',
            value: processedCount,
            hint: `This session · ${visibleOrders.length} total`,
        },
        {
            testid: 'kpi-approved-replenishments',
            label: 'Approved replenishments',
            value: approvedCount,
            hint: approvedCount > 0 ? 'Simulated scenario updated' : 'Nothing approved yet',
        },
    ];

    const setDecision = (orderId, sku, decision) => {
        setAllocationDecisions(prev => ({ ...prev, [`${orderId}:${sku}`]: decision }));
    };

    const allocateAll = () => {
        const next = { ...allocationDecisions };
        visibleOrders.forEach(o => o.lines.forEach(l => {
            next[`${o.order_id}:${l.sku}`] = 'allocated';
        }));
        setAllocationDecisions(next);
        toast.success('All pending lines allocated (simulated)');
    };

    const recommendedActionText = (s) => {
        const p = projectionBySku[s.sku];
        if (!p) return 'Review options';
        if (p.supplyGapDays === 0) return 'No gap at current scenario · consider proactive top-up';
        if (p.supplyGapDays > 20) return `Expedited manufacturing or transfer from Oslo recommended`;
        return `Transfer from Oslo recommended`;
    };

    const recStatus = (s) => {
        const p = projectionBySku[s.sku];
        return computeInsightStatus(
            { impact_type: 'revenue_at_risk', sku: s.sku },
            {
                projection: p,
                acknowledged: false,
                warehouseTransfer,
                leadTimeOverride,
                selectedStrategyForSku: !!selectedStrategy[s.suggestion_id],
                approvedForSku: !!approvedReplenishments[s.suggestion_id],
                hasOptions: (options_by_sku[s.sku] || []).length > 0,
            },
        );
    };

    const compareSuggestion = compareSku && suggestions.find(s => s.sku === compareSku);
    const compareOptions = (options_by_sku || {})[compareSku] || [];
    const compareProjection = projections.find(p => p.sku === compareSku);

    return (
        <div className="space-y-4" data-testid="retail-order-automation-page">
            <PageSummary metrics={summaryMetrics} />

            {/* Incoming retailer orders */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-900">Incoming retailer orders</h3>
                    {pendingCount > 0 && (
                        <Button variant="outline" size="sm" onClick={allocateAll} data-testid="allocate-all-button">
                            Allocate all
                        </Button>
                    )}
                </div>

                <Card className="border-slate-200 overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Order</TableHead>
                                <TableHead>Channel</TableHead>
                                <TableHead>SKU</TableHead>
                                <TableHead className="text-right">Qty</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {visibleOrders.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-slate-500 py-6 text-sm">
                                        No orders match the channels you've published to.
                                    </TableCell>
                                </TableRow>
                            )}
                            {visibleOrders.flatMap(order =>
                                order.lines.map((line, idx) => {
                                    const key = `${order.order_id}:${line.sku}`;
                                    const decision = allocationDecisions[key] ?? (order.status === 'allocated' ? 'allocated' : null);
                                    const ch = channelMap[order.channel_id];
                                    return (
                                        <TableRow key={key} data-testid={`order-line-${key}`}>
                                            {idx === 0 ? (
                                                <TableCell rowSpan={order.lines.length} className="font-medium align-top text-sm">
                                                    {order.order_id}
                                                </TableCell>
                                            ) : null}
                                            {idx === 0 ? (
                                                <TableCell rowSpan={order.lines.length} className="align-top text-sm">
                                                    {ch?.name || order.channel_id}
                                                </TableCell>
                                            ) : null}
                                            <TableCell className="font-mono text-xs">{line.sku}</TableCell>
                                            <TableCell className="text-right text-sm">{line.qty}</TableCell>
                                            <TableCell>
                                                {decision === 'allocated' ? (
                                                    <Badge className="bg-emerald-600 text-[10px]">Allocated</Badge>
                                                ) : decision === 'backorder' ? (
                                                    <Badge variant="outline" className="border-amber-400 text-amber-800 bg-amber-50 text-[10px]">Backorder</Badge>
                                                ) : (
                                                    <Badge variant="secondary" className="text-[10px]">Pending</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="inline-flex gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant={decision === 'allocated' ? 'default' : 'outline'}
                                                        onClick={() => setDecision(order.order_id, line.sku, 'allocated')}
                                                        className="h-7 px-2 text-xs"
                                                        data-testid={`allocate-${key}`}
                                                    >
                                                        <Check className="h-3 w-3 mr-1" />
                                                        Allocate
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant={decision === 'backorder' ? 'default' : 'outline'}
                                                        onClick={() => setDecision(order.order_id, line.sku, 'backorder')}
                                                        className="h-7 px-2 text-xs"
                                                        data-testid={`backorder-${key}`}
                                                    >
                                                        <ArrowDown className="h-3 w-3 mr-1" />
                                                        Backorder
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </Card>
            </div>

            {/* Replenishment recommendations (concise list) */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                        <Sparkles className="h-4 w-4 text-indigo-500" />
                        Replenishment recommendations
                    </h3>
                    <span className="text-xs text-slate-500">
                        {suggestions.length} SKU{suggestions.length === 1 ? '' : 's'} · tap to compare
                    </span>
                </div>

                <Card className="border-slate-200 overflow-hidden">
                    <div className="divide-y divide-slate-200">
                        {suggestions.map(s => {
                            const p = projectionBySku[s.sku];
                            const status = recStatus(s);
                            return (
                                <div
                                    key={s.suggestion_id}
                                    className="flex items-center gap-3 p-3"
                                    data-testid={`rec-row-${s.suggestion_id}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold text-slate-900">{s.sku}</span>
                                            <InsightStatusBadge status={status} testid={`rec-status-${s.suggestion_id}`} />
                                        </div>
                                        <div className="text-xs text-slate-600 mt-0.5">
                                            {p && p.supplyGapDays > 0
                                                ? <>{p.supplyGapDays}-day gap · {formatEur(p.revenueAtRisk)} at risk · suggesting {s.suggested_qty.toLocaleString()} units</>
                                                : <>No current gap · suggested top-up {s.suggested_qty.toLocaleString()} units</>
                                            }
                                        </div>
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            Recommended: {recommendedActionText(s)}
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setCompareSku(s.sku)}
                                        data-testid={`compare-rec-${s.suggestion_id}`}
                                    >
                                        Compare options
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            </div>

            <Card className="p-3 bg-slate-900 text-slate-200 border-slate-900">
                <div className="flex items-start gap-2 text-xs leading-relaxed">
                    <Workflow className="h-4 w-4 text-indigo-300 mt-0.5 flex-shrink-0" />
                    <span>
                        <strong className="text-white">Owntune orchestrates — never ships, stocks or sells.</strong>{' '}
                        Approvals here update the scenario only. No real supplier or retailer actions are triggered.
                    </span>
                </div>
            </Card>

            {/* Compare options drawer */}
            <Sheet open={!!compareSku} onOpenChange={(v) => { if (!v) setCompareSku(null); }}>
                <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
                    <SheetHeader>
                        <SheetTitle>Compare replenishment options</SheetTitle>
                    </SheetHeader>
                    {compareSuggestion && (
                        <div className="mt-4" data-testid={`compare-drawer-${compareSku}`}>
                            <SmartRecommendation
                                suggestion={compareSuggestion}
                                options={compareOptions}
                                projection={compareProjection}
                            />
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
}
